import { useEffect, useState, type ReactNode } from 'react';
import { api, ApiError } from '../api';

interface DealRow { id: string; name: string; amount: number; stage: string; expectedCloseDate: string | null; }
interface TaskRow { id: string; title: string; dueDate: string | null; priority: string; overdue: boolean; }
interface MeetingRow { id: string; title: string; startAt: string | null; location: string | null; }
interface ContactRow { id: string; name: string; email: string | null; title: string | null; }
interface PipelineRow { stage: string; count: number; amount: number; }
interface ActivityRow { type: string; count: number; }
interface Campaigns {
  totals: { sent: number; delivered: number; opened: number; clicked: number; bounced: number; complained: number; unsubscribed: number };
  rates: { openRate: number; clickRate: number; bounceRate: number; complaintRate: number; unsubscribeRate: number };
  recent: { id: string; name: string; status: string; sentAt: string | null; sent: number; delivered: number; opened: number; clicked: number; bounced: number }[];
}
interface Overview {
  scope: 'my' | 'team';
  canTeam: boolean;
  summary: { openCount: number; openValue: number; wonCount: number; wonValue: number; lostCount: number; lostValue: number };
  pipeline: PipelineRow[];
  closingThisMonth: { count: number; value: number; items: DealRow[] };
  tasks: { open: number; overdue: number; items: TaskRow[] };
  meetings: MeetingRow[];
  contacts: ContactRow[];
  openDeals: DealRow[];
  activities: ActivityRow[];
  campaigns: Campaigns | null;
}

const money = (n: number) => `$${n.toLocaleString()}`;
const day = (s: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const when = (s: string | null) => (s ? new Date(s).toLocaleString() : '—');
const DANGER = 'var(--danger, #c0392b)';

export function DashboardPage() {
  const [scope, setScope] = useState<'my' | 'team'>('my');
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    void api
      .get<Overview>(`/api/dashboard/overview?scope=${scope}`)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? `Couldn't load the dashboard (${e.status}).` : "Couldn't load the dashboard."));
  }, [scope]);

  if (error) return <div className="card"><h1>Dashboard</h1><p className="empty">{error}</p></div>;
  if (!data) return <div className="card"><h1>Dashboard</h1><p className="muted">Loading…</p></div>;

  const s = data.summary;
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <h1>Dashboard</h1>
        {data.canTeam && (
          <div className="row" style={{ gap: '0.5rem' }} role="group" aria-label="Scope">
            <ScopeButton active={scope === 'my'} onClick={() => setScope('my')}>My</ScopeButton>
            <ScopeButton active={scope === 'team'} onClick={() => setScope('team')}>Team</ScopeButton>
          </div>
        )}
      </div>
      <p className="muted">{scope === 'my' ? 'Your work' : 'Whole team'} · won/lost &amp; closing are for this month · cached ~30s.</p>

      <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
        <Stat label="Open deals" value={s.openCount} sub={money(s.openValue)} />
        <Stat label="Closing this month" value={data.closingThisMonth.count} sub={money(data.closingThisMonth.value)} />
        <Stat label="Won this month" value={s.wonCount} sub={money(s.wonValue)} />
        <Stat label="Lost this month" value={s.lostCount} sub={money(s.lostValue)} />
        <Stat label="Open tasks" value={data.tasks.open} />
        <Stat label="Overdue" value={data.tasks.overdue} danger={data.tasks.overdue > 0} />
      </div>

      <div className="row" style={{ gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start', marginTop: '1rem' }}>
        <Panel title="Closing this month">
          <DealList rows={data.closingThisMonth.items} empty="Nothing scheduled to close this month." />
        </Panel>

        <Panel title="My open tasks">
          {data.tasks.items.length === 0 ? <p className="empty">No open tasks.</p> : (
            <table className="grid">
              <thead><tr><th>Task</th><th>Due</th><th>Priority</th></tr></thead>
              <tbody>
                {data.tasks.items.map((t) => (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td style={t.overdue ? { color: DANGER } : undefined}>{day(t.dueDate)}{t.overdue ? ' ⚠' : ''}</td>
                    <td>{t.priority}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Upcoming meetings">
          {data.meetings.length === 0 ? <p className="empty">No upcoming meetings.</p> : (
            <table className="grid">
              <thead><tr><th>Meeting</th><th>When</th><th>Where</th></tr></thead>
              <tbody>{data.meetings.map((m) => <tr key={m.id}><td>{m.title}</td><td>{when(m.startAt)}</td><td>{m.location ?? '—'}</td></tr>)}</tbody>
            </table>
          )}
        </Panel>

        <Panel title={scope === 'my' ? 'My open deals' : 'Open deals'}>
          <DealList rows={data.openDeals} empty="No open deals." />
        </Panel>

        <Panel title={scope === 'my' ? 'My contacts' : 'Recent contacts'}>
          {data.contacts.length === 0 ? <p className="empty">No contacts.</p> : (
            <table className="grid">
              <thead><tr><th>Name</th><th>Title</th><th>Email</th></tr></thead>
              <tbody>{data.contacts.map((c) => <tr key={c.id}><td>{c.name}</td><td>{c.title ?? '—'}</td><td>{c.email ?? '—'}</td></tr>)}</tbody>
            </table>
          )}
        </Panel>

        <Panel title="Pipeline by stage">
          {data.pipeline.length === 0 ? <p className="empty">No open deals.</p> : (
            <table className="grid">
              <thead><tr><th>Stage</th><th>Deals</th><th>Value</th></tr></thead>
              <tbody>{data.pipeline.map((p) => <tr key={p.stage}><td>{p.stage}</td><td>{p.count}</td><td>{money(p.amount)}</td></tr>)}</tbody>
            </table>
          )}
        </Panel>
      </div>

      <h2 style={{ fontSize: '1.05rem', marginTop: '1.25rem' }}>Activity (30 days)</h2>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        {data.activities.map((a) => <span key={a.type} className="tag">{a.type}: {a.count}</span>)}
        {data.activities.length === 0 && <span className="empty">No recent activity.</span>}
      </div>

      {data.campaigns && (
        <>
          <h2 style={{ fontSize: '1.05rem', marginTop: '1.25rem' }}>
            Campaign KPIs <span className="muted" style={{ fontWeight: 400 }}>(all campaigns)</span>
          </h2>
          <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
            <Stat label="Sent" value={data.campaigns.totals.sent} />
            <Stat label="Delivered" value={data.campaigns.totals.delivered} />
            <Stat label="Open rate" value={`${data.campaigns.rates.openRate}%`} sub={`${data.campaigns.totals.opened} opens`} />
            <Stat label="Click rate" value={`${data.campaigns.rates.clickRate}%`} sub={`${data.campaigns.totals.clicked} clicks`} />
            <Stat label="Bounce rate" value={`${data.campaigns.rates.bounceRate}%`} sub={`${data.campaigns.totals.bounced} bounced`} danger={data.campaigns.rates.bounceRate >= 5} />
            <Stat label="Complaints" value={data.campaigns.totals.complained} danger={data.campaigns.totals.complained > 0} />
          </div>
          <table className="grid" style={{ marginTop: '0.75rem' }}>
            <thead><tr><th>Campaign</th><th>Status</th><th>Sent</th><th>Delivered</th><th>Opened</th><th>Clicked</th><th>Bounced</th><th>Sent at</th></tr></thead>
            <tbody>
              {data.campaigns.recent.map((c) => (
                <tr key={c.id}><td>{c.name}</td><td>{c.status}</td><td>{c.sent}</td><td>{c.delivered}</td><td>{c.opened}</td><td>{c.clicked}</td><td>{c.bounced}</td><td>{day(c.sentAt)}</td></tr>
              ))}
              {data.campaigns.recent.length === 0 && <tr><td colSpan={8} className="empty">No campaigns yet.</td></tr>}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function ScopeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '0.3rem 0.85rem',
        borderRadius: 6,
        border: '1px solid var(--border, #ccc)',
        cursor: 'pointer',
        fontWeight: active ? 700 : 400,
        background: active ? 'var(--accent, #1d4ed8)' : 'transparent',
        color: active ? '#fff' : 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card" style={{ flex: '1 1 360px', minWidth: 320 }}>
      <h2 style={{ fontSize: '1rem', marginTop: 0 }}>{title}</h2>
      {children}
    </div>
  );
}

function DealList({ rows, empty }: { rows: DealRow[]; empty: string }) {
  if (rows.length === 0) return <p className="empty">{empty}</p>;
  return (
    <table className="grid">
      <thead><tr><th>Deal</th><th>Stage</th><th>Value</th><th>Close</th></tr></thead>
      <tbody>{rows.map((d) => <tr key={d.id}><td>{d.name}</td><td>{d.stage}</td><td>{money(d.amount)}</td><td>{day(d.expectedCloseDate)}</td></tr>)}</tbody>
    </table>
  );
}

function Stat({ label, value, sub, danger }: { label: string; value: number | string; sub?: string; danger?: boolean }) {
  return (
    <div className="card" style={{ minWidth: 130 }}>
      <div className="muted">{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color: danger ? DANGER : undefined }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: '0.8rem' }}>{sub}</div>}
    </div>
  );
}
